import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useInfiniteScroll
 *
 * Slices `data` into pages and appends more rows when the sentinel
 * element (a div at the bottom of the list) enters the viewport.
 *
 * @param {Array}  data        - Full flat array of records
 * @param {number} pageSize    - How many rows to load per batch (default 20)
 * @returns {{
 *   visibleData: Array,       - Currently visible slice
 *   sentinelRef: RefObject,   - Attach to a <div> at the bottom of the list
 *   hasMore: boolean,         - Whether more records exist
 *   reset: () => void         - Call when data changes to restart from page 1
 * }}
 */
const useInfiniteScroll = (data = [], pageSize = 20) => {
  const [page, setPage] = useState(1);
  const sentinelRef = useRef(null);

  // Reset to page 1 whenever the source data changes
  const reset = useCallback(() => setPage(1), []);
  useEffect(() => {
    reset();
  }, [data, reset]);

  const visibleData = data.slice(0, page * pageSize);
  const hasMore = visibleData.length < data.length;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, visibleData.length]);

  return { visibleData, sentinelRef, hasMore, reset };
};

export default useInfiniteScroll;
